"use client";

import { ImageEffectSettings } from "@saas/shared/components/ImageEffectSettings";
import { GemIcon } from "lucide-react";

import { IMAGE_HERO_EXAMPLE_EVENT } from "@marketing/home/components/HeroControls";
import type { ImageHeroExample } from "@marketing/image-generator/components/ImageGeneratorHero";
import { ImageEffectWorkbench } from "@saas/shared/components/ImageEffectWorkbench";

import { useSession } from "@saas/auth/hooks/use-session";
import {
	CreditsPaywallModal,
	type CreditsPaywallReason,
} from "@saas/payments/components/CreditsPaywallModal";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { ImageCompareItem } from "@saas/shared/components/carousel/image-compare-item";
import { useRouter } from "@shared/hooks/router";
import { compressImage } from "@shared/lib/image-compression";
import {
	PENDING_TRANSFORMATION_STATE_KEY,
	saveBlobPendingState,
} from "@shared/lib/pending-state";
import {
	addCanvasWatermark,
	shouldAddWatermarkOnClient,
} from "@shared/lib/watermark-canvas";
import { Card, CardContent } from "@ui/components/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { Input } from "@ui/components/input";
import { Label } from "@ui/components/label";
import { Slider } from "@ui/components/slider";
import { cn } from "@ui/lib";
import { ChevronDownIcon, ImageIcon, X, ZoomIn } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import {
	FILE_ERROR_MESSAGES,
	FILE_UPLOAD_LIMITS,
} from "../constants/file-upload";
import {
	TRANSFORMATION_PROMPTS,
	TRANSFORMATION_THEMES,
} from "../constants/transformation";
import { WATERMARK_CONFIG } from "../constants/watermark";
import { useImageTransformation } from "../hooks/use-image-transformation";
import type { TransformationType } from "../hooks/use-image-transformation";

interface ImageTransformationClientProps {
	type: TransformationType;
	exampleImages: {
		beforeSrc: string;
		afterSrc: string;
		altBefore: string;
		altAfter: string;
	};
}

// Aspect ratios for photo-to-pixar (8 ratios, including original)
const PIXAR_ASPECT_RATIOS = [
	{ label: "Original", value: "original" },
	{ label: "1:1", value: "1:1" },
	{ label: "16:9", value: "16:9" },
	{ label: "9:16", value: "9:16" },
	{ label: "3:4", value: "3:4" },
	{ label: "4:3", value: "4:3" },
	{ label: "3:2", value: "3:2" },
	{ label: "2:3", value: "2:3" },
] as const;

const DEFAULT_AGE_VALUE = 25;

// Helper function to get aspect ratio dimensions for preview
const getRatioPreviewDimensions = (value: string) => {
	const PREVIEW_WIDTH = 24; // Fixed width for all previews

	// Original ratio - show a square
	if (value === "original") {
		return { width: PREVIEW_WIDTH, height: PREVIEW_WIDTH };
	}

	const [w, h] = value.split(":").map(Number);
	if (!w || !h) {
		return { width: PREVIEW_WIDTH, height: PREVIEW_WIDTH };
	}

	// Calculate height based on ratio, maintaining fixed width
	const height = Math.round((PREVIEW_WIDTH * h) / w);
	return { width: PREVIEW_WIDTH, height };
};

// Map an age-slider value (5–80) to the descriptive text sent to the model.
function ageTextForValue(age: number): string {
	if (age <= 5) return "as a toddler";
	if (age <= 12) return `as a ${age} year old child`;
	if (age <= 19) return "as a teenager";
	if (age <= 25) return `as a ${age} year old`;
	if (age <= 35) return "in their early 30s";
	if (age <= 45) return "in their early 40s";
	if (age <= 55) return "in their early 50s";
	if (age <= 65) return "in their early 60s";
	if (age <= 75) return "in their early 70s";
	return "in their 80s";
}

function clampAgeValue(age: number): number {
	return Math.max(5, Math.min(80, Math.round(age)));
}

function ageValueFromText(ageText: string): number | null {
	const normalized = ageText.toLowerCase();
	const explicitAge = normalized.match(
		/\b(\d{1,3})\s*(?:year|years)\s*old\b/,
	);

	if (explicitAge) {
		return clampAgeValue(Number(explicitAge[1]));
	}

	if (normalized.includes("toddler")) return 5;
	if (normalized.includes("child")) return 8;
	if (normalized.includes("teen")) return 16;
	if (normalized.includes("early 20s")) return 25;
	if (normalized.includes("early 30s") || normalized.includes("30s"))
		return 35;
	if (normalized.includes("early 40s") || normalized.includes("40s"))
		return 45;
	if (normalized.includes("early 50s") || normalized.includes("50s"))
		return 55;
	if (normalized.includes("early 60s") || normalized.includes("60s"))
		return 65;
	if (normalized.includes("early 70s") || normalized.includes("70s"))
		return 75;
	if (normalized.includes("80s")) return 80;

	return null;
}

export function ImageTransformationClient({
	type,
	exampleImages,
}: ImageTransformationClientProps) {
	const [zoomImage, setZoomImage] = useState<string | null>(null);
	const [generationProgress, setGenerationProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false); // Track image upload state
	// Slider position for the age filter; maps to the descriptive ageText below.
	const [ageValue, setAgeValue] = useState(DEFAULT_AGE_VALUE);
	const sliderAgeTextRef = useRef<string | null>(null);
	const syncedRestoredAgeValueRef = useRef<number | null>(null);
	const router = useRouter();
	const { user } = useSession();

	// Get theme based on type
	const theme = useMemo(() => TRANSFORMATION_THEMES[type], [type]);

	// Get translation key based on type
	const translationKey = useMemo(() => {
		switch (type) {
			case "pixar":
				return "imageToPixar";
			case "anime":
				return "imageToAnime";
			case "cartoon":
				return "imageToCartoon";
			case "age":
				return "ageFilter";
			default:
				return "imageToAnime";
		}
	}, [type]);

	const t = useTranslations(`${translationKey}.client`);
	const tForm = useTranslations("imageTransformation");
	const tPage = useTranslations(translationKey);
	const tActions = useTranslations("assets.buttons");

	const {
		imagePreviewUrl,
		generatedImageUrl,
		originalGeneratedImageUrl,
		watermark,
		aspectRatio,
		resolution,
		setResolution,
		creditCost,
		lockedResolutions,
		customPrompt,
		isFullPrompt,
		usesRemaining,
		hasUnlimitedCredits,
		isPending,
		handleImageSelected,
		handleGenerateTransformation,
		clearGeneratedImage,
		setAspectRatio,
		setCustomPrompt,
		setIsFullPrompt,
		ageText,
		setAgeText,
		restoredAgeValue,
		showUpgradeModal,
		setShowUpgradeModal,
	} = useImageTransformation(type);

	useEffect(() => {
		let controller: AbortController | undefined;
		const onExample = async (event: Event) => {
			const example = (event as CustomEvent<ImageHeroExample>).detail;
			const age = ageValueFromText(example.prompt);
			if (!example.images[0] || (type === "age" && age === null)) return;
			controller?.abort();
			const request = new AbortController();
			controller = request;
			setIsUploading(true);
			try {
				const response = await fetch(example.images[0], {
					signal: request.signal,
				});
				if (!response.ok) throw new Error("Failed to load example image");
				const blob = await response.blob();
				if (request.signal.aborted) return;
				handleImageSelected(
					new File([blob], `${type}-example.webp`, { type: blob.type }),
				);
				if (type === "age" && age !== null) {
					setAgeValue(age);
					setAgeText(`as a ${age} year old`);
					setCustomPrompt("");
					setIsFullPrompt(false);
				} else if (type !== "age") {
					const base = TRANSFORMATION_PROMPTS[type];
					setIsFullPrompt(!example.prompt.startsWith(base));
					setCustomPrompt(example.prompt.startsWith(base)
						? example.prompt.slice(base.length).replace(/^,\s*/, "")
						: example.prompt);
				}
				setResolution("1K");
				setAspectRatio(example.aspectRatio ?? "original");
			} catch {
				if (!request.signal.aborted) toast.error(t("errors.imageProcessing"));
			} finally {
				if (!request.signal.aborted) setIsUploading(false);
			}
		};
		window.addEventListener(IMAGE_HERO_EXAMPLE_EVENT, onExample);
		return () => {
			controller?.abort();
			window.removeEventListener(IMAGE_HERO_EXAMPLE_EVENT, onExample);
		};
	}, [type, handleImageSelected, setAgeText, setCustomPrompt, setIsFullPrompt, setAspectRatio, t]);

	// Get user subscription status
	const { canRemoveWatermark } = usePurchases();

	// Track why the paywall opened so it shows the right copy.
	const [upgradeReason, setUpgradeReason] =
		useState<CreditsPaywallReason | null>(null);

	// When the hook opens the modal (insufficient credits) without a reason,
	// default to the out-of-credits copy.
	useEffect(() => {
		if (showUpgradeModal && !upgradeReason) {
			setUpgradeReason("insufficient-credits");
		}
	}, [showUpgradeModal, upgradeReason]);

	// Keep the age slider display aligned when pending state restores age data.
	useEffect(() => {
		if (type !== "age") return;

		if (
			typeof restoredAgeValue === "number" &&
			syncedRestoredAgeValueRef.current !== restoredAgeValue
		) {
			setAgeValue(clampAgeValue(restoredAgeValue));
			syncedRestoredAgeValueRef.current = restoredAgeValue;
			return;
		}

		if (!ageText) return;
		if (sliderAgeTextRef.current === ageText) {
			sliderAgeTextRef.current = null;
			return;
		}

		const restoredAge = ageValueFromText(ageText);
		if (restoredAge !== null) {
			setAgeValue(restoredAge);
		}
	}, [type, ageText, restoredAgeValue]);

	// Handle download with watermark
	const handleDownload = useCallback(async () => {
		if (!originalGeneratedImageUrl) return;

		try {
			// Use proxy endpoint to avoid CORS issues
			const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(originalGeneratedImageUrl)}`;
			const response = await fetch(proxyUrl);

			if (!response.ok) {
				throw new Error("Failed to fetch image");
			}

			const imageBlob = await response.blob();

			let finalBlob = imageBlob;

			// Add watermark if needed
			if (shouldAddWatermarkOnClient(canRemoveWatermark, watermark)) {
				try {
					finalBlob = await addCanvasWatermark(
						imageBlob,
						WATERMARK_CONFIG,
					);
				} catch (watermarkError) {
					console.warn("Failed to add watermark:", watermarkError);
					// Use original if watermark fails
					finalBlob = imageBlob;
				}
			}

			// Create download URL from blob
			const downloadUrl = URL.createObjectURL(finalBlob);

			// Download the image
			const link = document.createElement("a");
			link.href = downloadUrl;
			link.download = `${type}-${Date.now()}.jpg`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			// Clean up the URL
			URL.revokeObjectURL(downloadUrl);
		} catch (error) {
			console.error("Error downloading image:", error);
			toast.error(t("errors.download"));
		}
	}, [originalGeneratedImageUrl, canRemoveWatermark, watermark, type, t]);

	// Handle Editor button click
	const handleMagicEdit = useCallback(() => {
		if (!generatedImageUrl) return;

		// Store the generated image URL for editing
		if (typeof window !== "undefined") {
			localStorage.setItem("editImageUrl", generatedImageUrl);
		}
		router.push("/ai-edit-image");
	}, [generatedImageUrl, router]);

	// File upload with compression
	const { getRootProps, getInputProps } = useDropzone({
		maxSize: FILE_UPLOAD_LIMITS.MAX_SIZE,
		onDrop: async (acceptedFiles) => {
			if (acceptedFiles.length > 0) {
				const file = acceptedFiles[0];
				setIsUploading(true);

				try {
					// Compress image silently
					const compressedFile = await compressImage(file);
					handleImageSelected(compressedFile);
					setIsUploading(false);
				} catch (error) {
					console.error(
						FILE_ERROR_MESSAGES.COMPRESSION_FAILED,
						error,
					);
					// If compression fails, still use the original file
					handleImageSelected(file);
					setIsUploading(false);
				}
			}
		},
		onDropRejected: (fileRejections) => {
			if (fileRejections.length > 0) {
				const error = fileRejections[0].errors[0];
				if (error.code === "file-too-large") {
					toast.error(t("errors.fileTooLarge"));
				} else {
					toast.error(t("errors.invalidFile"));
				}
			}
		},
		accept: FILE_UPLOAD_LIMITS.ACCEPTED_TYPES,
		multiple: false,
	});

	const handleGetMoreCredits = useCallback(() => {
		setUpgradeReason("insufficient-credits");
		setShowUpgradeModal(true);
	}, [setShowUpgradeModal]);

	const handleViewMyArts = useCallback(() => {
		router.push("/my-assets");
	}, [router]);

	const handleButtonClick = useCallback(async () => {
		// Check if user is logged in
		if (!user) {
			// Save current state to localStorage before triggering login
			if (typeof window !== "undefined" && imagePreviewUrl) {
				try {
					// Blob 直接落 IndexedDB，不用再转 base64
					const blob = await (await fetch(imagePreviewUrl)).blob();

					await saveBlobPendingState(
						PENDING_TRANSFORMATION_STATE_KEY,
						{
							type,
							imageBlob: blob,
							aspectRatio,
							resolution,
							customPrompt,
							isFullPrompt,
							watermark,
							ageText: type === "age" ? ageText : undefined,
							ageValue: type === "age" ? ageValue : undefined,
						},
					);
				} catch (error) {
					console.error(
						"Failed to save transformation state:",
						error,
					);
				}
			}

			// Trigger login dialog instead of redirect
			if (typeof window !== "undefined") {
				const redirectPath =
					window.location.pathname + window.location.search;
				console.log("Triggering login with redirect to:", redirectPath);
				window.dispatchEvent(
					new CustomEvent("show-login", {
						detail: { redirectTo: redirectPath },
					}),
				);
			}
			return;
		}

		// User is logged in, proceed with generation or redirect
		// For pixar type, this will redirect to ai-image-generator
		// For other types, this will generate directly
		// Start progress animation
		setGenerationProgress(0);
		handleGenerateTransformation();
	}, [
		user,
		handleGenerateTransformation,
		imagePreviewUrl,
		type,
		aspectRatio,
		resolution,
		customPrompt,
		isFullPrompt,
		watermark,
		ageText,
		ageValue,
	]);

	const handleZoomClose = useCallback(() => {
		setZoomImage(null);
	}, []);

	const handleClearGeneratedImage = useCallback(() => {
		clearGeneratedImage();
		setGenerationProgress(0);
	}, [clearGeneratedImage]);

	// Handle progress animation
	useEffect(() => {
		let interval: NodeJS.Timeout;

		if (isPending) {
			// Start progress animation when generation starts
			interval = setInterval(() => {
				setGenerationProgress((prev) => {
					if (prev >= 90) {
						return 90; // Cap at 90% until completion
					}
					return prev + Math.random() * 15;
				});
			}, 500);
		}

		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [isPending]);

	// Reset progress when generation completes
	useEffect(() => {
		if (generatedImageUrl && !isPending) {
			setGenerationProgress(100);
			setTimeout(() => setGenerationProgress(0), 500);
		}
	}, [generatedImageUrl, isPending]);

	// Get alt text based on type
	const generatedAltText = useMemo(() => {
		switch (type) {
			case "pixar":
				return t("pixarImageAlt");
			case "anime":
				return t("generatedAnimeAlt");
			case "cartoon":
				return t("cartoonImageAlt");
			case "age":
				return t("generatedAgeAlt");
			default:
				return t("generatedImageAlt");
		}
	}, [type, t]);

	return (
		<div className="space-y-6">
			<ImageEffectWorkbench
				href={type === "age" ? "/age-filter" : `/photo-to-${type}`}
				description={tPage("description")}
				action={
					<button
						type="button"
						className="flex h-12 w-full items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground disabled:opacity-50"
						onClick={handleButtonClick}
						disabled={
							isPending ||
							!imagePreviewUrl ||
							(type === "age" && !ageText)
						}
					>
						{isPending ? (
							<>
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white mr-2" />
								{tForm("generating")}
							</>
						) : (
							<>{tForm("create")}{user && <><GemIcon className="ml-2 mr-1 h-3.5 w-3.5" /><span data-testid="generation-credit-cost">{creditCost}</span></>}</>
						)}
					</button>
				}
				preview={
					generatedImageUrl ? (
						<div className="w-full flex flex-col">
							<div className="w-full flex-1">
								<Card
									className={`${theme.border} shadow-sm w-full h-full`}
								>
									<CardContent className="p-4 h-full">
										<div className="w-full h-full bg-white overflow-hidden rounded-lg flex items-center justify-center relative group">
											<img
												src={generatedImageUrl}
												alt={generatedAltText}
												className="max-w-full max-h-full object-contain"
											/>
											<div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
												<button
													type="button"
													onClick={handleDownload}
													className="rounded-full bg-white/90 px-3 py-2 text-sm shadow"
												>
													{tActions("download")}
												</button>
												<button
													type="button"
													onClick={
														handleClearGeneratedImage
													}
													className="bg-white/80 hover:bg-white text-gray-800 shadow-lg rounded-full p-2 transition-colors"
													aria-label={tForm(
														"removeGeneratedImage",
													)}
												>
													<X className="h-4 w-4" />
												</button>
												<button
													type="button"
													onClick={() =>
														setZoomImage(
															generatedImageUrl,
														)
													}
													className="bg-white/80 hover:bg-white text-gray-800 shadow-lg rounded-full p-2 transition-colors"
													aria-label={tForm(
														"zoomGeneratedImage",
													)}
												>
													<ZoomIn className="h-4 w-4" />
												</button>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>
					) : (
						<div
							className="mx-auto flex flex-col"
							style={{ width: "min(100%, 480px, calc((100dvh - 160px) * 0.75))" }}
						>
							<div className="w-full flex-1 rounded-lg border-2 border-solid border-slate-200 flex items-center justify-center bg-white overflow-hidden">
								{isPending ? (
									<div className="text-center flex flex-col items-center justify-center h-full">
										{/* DaisyUI-style Radial Progress */}
										<div
											className={`radial-progress ${theme.progressColor}`}
											style={
												{
													"--value":
														Math.round(
															generationProgress,
														),
													"--size": "120px",
													"--thickness": "8px",
												} as React.CSSProperties
											}
											role="progressbar"
											aria-valuenow={Math.round(
												generationProgress,
											)}
										>
											<span className="text-2xl font-bold">
												{Math.round(generationProgress)}
												%
											</span>
										</div>

										<p className="text-gray-700 dark:text-gray-300 font-medium text-lg mt-6 mb-2">
											{t("generatingTitle")}
										</p>
										<p className="text-sm text-gray-500 dark:text-gray-400">
											{tForm("pleaseWait")}
										</p>
									</div>
								) : (
									<ImageCompareItem
										beforeSrc={exampleImages.beforeSrc}
										afterSrc={exampleImages.afterSrc}
										altBefore={exampleImages.altBefore}
										altAfter={exampleImages.altAfter}
									/>
								)}
							</div>
						</div>
					)
				}
			>
				{/* Image Upload Area */}
				<div className="flex items-center justify-center w-full h-[200px] flex-shrink-0 bg-white overflow-hidden mb-4">
					{isUploading ? (
						<div className="flex flex-col items-center justify-center gap-4">
							<span
								className={`loading loading-infinity loading-lg ${theme.progressColor}`}
							/>
							<p className="text-sm text-gray-500">
								{t("status.uploading")}
							</p>
						</div>
					) : imagePreviewUrl ? (
						<div className="relative w-full h-full flex items-center justify-center overflow-hidden group">
							<img
								src={imagePreviewUrl}
								alt={t("originalImageAlt")}
								className="max-w-full max-h-full object-contain"
							/>
							<div className="absolute top-2 right-2 flex gap-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
								<button
									type="button"
									onClick={clearGeneratedImage}
									className="bg-white/80 hover:bg-white text-gray-800 shadow-lg rounded-full p-2 transition-colors"
									aria-label={tForm("removeUploadedImage")}
								>
									<X className="h-4 w-4" />
								</button>
								<button
									type="button"
									onClick={() =>
										setZoomImage(imagePreviewUrl)
									}
									className="bg-white/80 hover:bg-white text-gray-800 shadow-lg rounded-full p-2 transition-colors"
									aria-label={tForm("zoomImage")}
								>
									<ZoomIn className="h-4 w-4" />
								</button>
							</div>
						</div>
					) : (
						<div
							{...getRootProps()}
							className={`flex items-center justify-center h-full w-full bg-transparent cursor-pointer transition-colors border-2 border-dashed border-gray-200 ${theme.hover} ${theme.bg}/20 rounded-lg`}
						>
							<input {...getInputProps()} />
							<div className="flex flex-col items-center justify-center text-center p-4">
								<ImageIcon
									className={`${theme.iconColor} mb-2 h-8 w-8`}
									aria-label={tForm("imagePlaceholder")}
								/>
								<p className="text-gray-400 font-medium">
									{tForm("uploadPhoto")}
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									{tForm("fileFormats")}
								</p>
							</div>
						</div>
					)}
				</div>

				{/* Form Fields */}
				<div className="space-y-3 flex flex-col">
					{/* Age slider (age filter only) — maps to ageText */}
					{type === "age" && (
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label className="text-sm font-medium text-gray-700">
									Age
								</Label>
								<span className="text-sm font-medium text-indigo-600">
									{ageValue} years
								</span>
							</div>
							<Slider
								value={[ageValue]}
								onValueChange={(values) => {
									const age = values[0];
									const nextAgeText = ageTextForValue(age);
									setAgeValue(age);
									sliderAgeTextRef.current = nextAgeText;
									setAgeText(nextAgeText);
								}}
								min={5}
								max={80}
								step={1}
								className="w-full"
							/>
							<div className="flex justify-between text-xs text-gray-500">
								<span>Baby</span>
								<span>Child</span>
								<span>Adult</span>
								<span>Senior</span>
								<span>Elder</span>
							</div>
						</div>
					)}

					{/* Additional Prompt (all types) */}
					<div className="space-y-1.5">
						<Label className="text-sm font-medium text-gray-700">
							{tForm("additionalPrompt")}
						</Label>
						<Input
							type="text"
							value={customPrompt}
							onChange={(e) => setCustomPrompt(e.target.value)}
							placeholder={
								type === "anime"
									? tForm("animePromptPlaceholder")
									: tForm("promptPlaceholder")
							}
							className="w-full h-9 text-sm"
						/>
					</div>

					{type === "pixar" ? (
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">
							{t("settings.aspectRatio")}
						</Label>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									type="button"
									className="flex items-center justify-between px-3 py-2 text-sm bg-zinc-100 border border-transparent rounded-xl hover:bg-zinc-200/70 transition-colors w-full"
								>
									<span className="truncate">
										{PIXAR_ASPECT_RATIOS.find((r) => {
											// Map "match_input_image" to "original" for display
											const displayValue =
												aspectRatio ===
												"match_input_image"
													? "original"
													: aspectRatio;
											return displayValue === r.value;
										})?.label || "Original"}
									</span>
									<ChevronDownIcon className="h-4 w-4 ml-2 flex-shrink-0" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								className="w-[280px] p-2"
								align="start"
							>
								<div className="grid grid-cols-4 gap-2">
									{PIXAR_ASPECT_RATIOS.map((ratio) => {
										const { width, height } =
											getRatioPreviewDimensions(
												ratio.value,
											);
										// Map "original" to "match_input_image" for comparison
										const currentValue =
											aspectRatio === "match_input_image"
												? "original"
												: aspectRatio;
										const isSelected =
											currentValue === ratio.value;
										return (
											<button
												type="button"
												key={ratio.value}
												onClick={() => {
													// Map "original" back to "match_input_image" for anime and age types
													const valueToSet =
														ratio.value ===
															"original" &&
														type !== "pixar"
															? "match_input_image"
															: ratio.value;
													setAspectRatio(valueToSet);
												}}
												className={cn(
													"flex flex-col items-center justify-center gap-1.5 rounded-lg text-[10px] transition-all border py-2",
													isSelected
														? "bg-blue-50 border-blue-500 text-blue-600 shadow-sm"
														: "bg-muted/10 border-border text-muted-foreground hover:bg-muted/20 hover:border-zinc-300",
												)}
											>
												<div
													className={cn(
														"rounded-[1.5px] transition-colors",
														isSelected
															? "bg-blue-500"
															: "bg-zinc-400",
													)}
													style={{
														width: `${width}px`,
														height: `${height}px`,
													}}
												/>
												<span className="font-medium">
													{ratio.label}
												</span>
											</button>
										);
									})}
								</div>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
					) : (
					<ImageEffectSettings
						aspectRatio={aspectRatio}
						setAspectRatio={setAspectRatio}
						resolution={resolution}
						setResolution={setResolution}
						lockedResolutions={lockedResolutions}
						onLockedResolution={() => {
							setUpgradeReason("image-resolution");
							setShowUpgradeModal(true);
						}}
					/>
					)}
				</div>
			</ImageEffectWorkbench>

			{/* Zoom Modal */}
			{zoomImage && (
				<div
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
					onClick={handleZoomClose}
					role="dialog"
					aria-modal="true"
					aria-label={t("zoomedImageAlt")}
				>
					<div
						className="relative max-w-4xl max-h-[90vh] p-4"
						onClick={(e) => e.stopPropagation()}
					>
						<button
							type="button"
							onClick={handleZoomClose}
							className="absolute top-2 right-2 bg-white rounded-full p-2 z-10 hover:bg-gray-100"
							aria-label={tForm("closeZoom")}
						>
							<X className="h-4 w-4" />
						</button>
						<img
							src={zoomImage}
							alt={t("zoomedImageAlt")}
							className="max-w-full max-h-full object-contain rounded-lg"
						/>
					</div>
				</div>
			)}

			{/* Upgrade Modal */}
			<CreditsPaywallModal
				open={showUpgradeModal}
				onOpenChange={(open) => {
					setShowUpgradeModal(open);
					if (!open) {
						setUpgradeReason(null);
					}
				}}
				reason={upgradeReason}
			/>

			{/* Add custom CSS for palette animation */}
			<style jsx>{`
				@keyframes palette-paint {
					0%,
					100% {
						transform: rotate(0deg);
					}
					50% {
						transform: rotate(15deg);
					}
				}
			`}</style>
		</div>
	);
}
